// ============================================================
// Rechenteil der Karte — reine Funktionen, kein MapLibre, kein React.
//
// Hier liegt alles, was man ohne Browser nachrechnen kann: das gemeinsame
// Modell fuer beide Betriebsarten (angemeldet / Schaufenster), der Filter,
// die Bogenform, die Plaetze der Avatare und das Einpassen der Kamera.
// Getrennt gehalten, damit die drei grossen Dateien daneben (Ebenen,
// Avatare, Karte) nur noch Zustand und Nebenwirkungen enthalten.
// ============================================================
import type {
  Abgabe,
  Buero,
  Freigabe,
  LonLat,
  OeffentlichBuero,
  OeffentlichProjekt,
  OeffentlichStand,
  Person,
  Phase,
  ProjektArt,
  ProjektTeam,
  ProjektKurz,
  Stand,
} from "../../api/types";
import type { Filter, SteckbriefArt } from "../../store/ui";
import { gehoertZuBuero } from "../../lib/bueros";
import { projektPraefix, projektTitel, tageBis } from "../../lib/format";
import { phasenName, projektArtName, projektTeamName } from "../../lib/texte";

/* ================================================================ Modell ==
   `Stand` und `OeffentlichStand` tragen dieselbe Karte, aber nicht dieselben
   Felder: oeffentlich gibt es keine Personen, keine Abgaben und keine
   Bearbeitungsrechte (E21). Statt jede Ebene und jedes Blatt zweimal zu
   schreiben, wird BEIDES einmal in diese zwei Formen uebersetzt. Alles
   darunter kennt nur noch `Stift` und `Ortsmarke` und muss nicht wissen, ob
   jemand angemeldet ist.                                                   */

/** Ein Projekt, so wie die Karte es braucht. */
export interface Stift {
  id: string;
  name: string;
  /** Projektnummer — im Schaufenster leer; die Freitextsuche trifft sie mit. */
  nummer: string;
  /** Hauskürzel (006) — im Schaufenster leer; die Freitextsuche trifft es mit. */
  kuerzel: string;
  /**
   * Der Anzeigetitel „KUE 2026-017 · Name" (SPEC §11.1) und sein Mono-Präfix,
   * EINMAL gerechnet statt an jeder Stelle, die einen Namen zeichnet.
   * Im Schaufenster stehen beide ohne Kürzel und Nummer da: nach aussen heisst
   * ein Projekt nur bei seinem Namen (E21).
   */
  titel: string;
  praefix: string;
  ort: string;
  /** Projektart — Filterchips und die Zeile auf der Auswahlkarte. */
  straenge: ProjektArt[];
  /** Team des Hauses (007); `null` heisst „keinem zugeordnet". */
  team: ProjektTeam | null;
  /** Konzeptfortschritt (007); `null` bei Objektplanung und bei leerer Liste.
   *  Im Schaufenster immer `null` — nach aussen zeigt das Haus keine Haken. */
  fortschritt: { erledigt: number; in_arbeit: number; gesamt: number } | null;
  punkt: LonLat;
  phaseId: string;
  /** Alle Phasen des Projekts, die Startphase eingeschlossen. Die Galerie sucht
   *  darin die aktuelle Phase JE STRANG — bei einem Projekt mit Konzept UND
   *  Objektplanung ist die Startphase nur eine von beiden. */
  phasenIds: string[];
  /**
   * Farbe des FEDERFUEHRENDEN Bueros (007, SPEC §12.3) — Stift, Bogenende,
   * Plakette, Auswahlkarte und Galerie-Kachelkante teilen sie.
   *
   * Bis zum Nachtrag II stand hier die Phasenfarbe. Der Tausch hat einen
   * Grund: auf der Karte laufen die Boegen VOM Buero ZUM Projekt, und ein
   * Bogen, der in Buerofarbe beginnt und in Phasenfarbe endet, beantwortet
   * eine Frage, die niemand gestellt hat. Die Phase bleibt sichtbar — als
   * `PhasenChip` in Phasenfarbe, und auf dem Brett als eigene Spalte.
   *
   * Rueckfall: erstes Buero, dann `--accent`. Ein Projekt OHNE Buero gibt es
   * laut E12 nicht, aber ein Stift ohne Farbe waere unsichtbar, und ein
   * unsichtbarer Stift ist der schlechteste Weg, einen Datenfehler zu melden.
   */
  farbe: string;
  bueroIds: string[];
  /** federfuehrendes Buero zuerst; oeffentlich genauso. */
  fuehrend: string | null;
  /** Nutzerkennungen der Teammitglieder; im Schaufenster immer leer. */
  mitglieder: string[];
  /** Naechste offene Abgabe faellig in <= 7 Tagen (E11) — der Pulsring. */
  bald: boolean;
  /** Tage bis zur naechsten offenen Abgabe, `null` wenn keine offen ist. */
  tage: number | null;
  abgabeTitel: string | null;
  /**
   * Alles, was die Freitextsuche an diesem Projekt treffen darf, kleingeschrieben
   * und EINMAL gebaut: Name, Kuerzel, Nummer, Ort, naechste Abgabe, Phase (de+en),
   * Projektart und Team (de+en), Bueros. „Entwurf", „LP 3", „Wettbewerb" oder
   * „Hamburg" finden so ihre Projekte, nicht nur der Name.
   */
  suchtext: string;
  titelbild: string | null;
  oeffentlich: boolean;
  /**
   * §36: ob das Projekt schon im Haus steht. Galerie, Board und Karte zeichnen
   * daraus den leisen Chip „Freigabe offen" / „Abgelehnt" (`FreigabeChip`).
   * Der Kartenstift selbst bleibt, wie er ist — die Lage stimmt ja, und wer
   * einen nicht freigegebenen Stift sieht, darf ihn sehen (anlegende Person
   * oder Freigebende; `/stand` liefert ihn niemandem sonst).
   * Im Schaufenster immer „freigegeben": nach aussen steht nur, was das Haus zeigt.
   */
  freigabe: Freigabe;
  /** Geodaetisch gerechnet (T9); `null` ohne Projektgrenze. */
  flaecheM2: number | null;
}

/** Ein Buero, so wie die Karte es braucht. */
export interface Ortsmarke {
  id: string;
  name: string;
  farbe: string;
  punkt: LonLat;
  /** Zahl der Personen mit diesem Heimatbuero; im Schaufenster 0. */
  kopfzahl: number;
  projektZahl: number;
}

const GRAU = "#717877";

export function phasenFarbe(phasen: readonly Phase[], id: string): string {
  const p = phasen.find((q) => q.id === id);
  return p ? p.farbe : GRAU;
}

/**
 * Die Farbtafel der Bueros — `{ Kennung: "#rrggbb" }`.
 *
 * Sie wird EINMAL je Stand gebaut und nicht je Stift: `stiftAusProjekt` laeuft
 * ueber alle Projekte, und ein `bueros.find()` darin waere bei 150 Projekten
 * und acht Bueros eine Suche zu viel.
 */
export function bueroFarbtafel(bueros: readonly { id: string; farbe: string }[]): Map<string, string> {
  return new Map(bueros.map((b) => [b.id, b.farbe]));
}

/** Die Farbe des federfuehrenden Bueros, sonst die des ersten, sonst `--accent`. */
function projektFarbe(
  bueros: readonly { buero_id: string; federfuehrend: boolean }[],
  farben: ReadonlyMap<string, string>,
): string {
  for (const id of bueroReihenfolge(bueros)) {
    const farbe = farben.get(id);
    if (farbe) return farbe;
  }
  return cssWert("--accent", GRAU);
}

function fuehrendesBuero(bueros: readonly { buero_id: string; federfuehrend: boolean }[]): string | null {
  const f = bueros.find((b) => b.federfuehrend);
  if (f) return f.buero_id;
  const erstes = bueros[0];
  return erstes ? erstes.buero_id : null;
}

/** Namen, die die Freitextsuche neben den Projektfeldern treffen soll. */
export interface Suchnamen {
  /** Phasenkennung → „LP 3 · Entwurfsplanung · Design development" (beide Sprachen). */
  phasen: ReadonlyMap<string, string>;
  /** Buerokennung → Bueroname. */
  bueros: ReadonlyMap<string, string>;
}

export const LEERE_NAMEN: Suchnamen = { phasen: new Map(), bueros: new Map() };

export function suchnamen(phasen: readonly Phase[], bueros: readonly { id: string; name: string }[]): Suchnamen {
  return {
    phasen: new Map(phasen.map((p) => [p.id, `${p.kurz} ${phasenName(p, "de")} ${phasenName(p, "en")}`])),
    bueros: new Map(bueros.map((b) => [b.id, b.name])),
  };
}

/** Beide Sprachen, weil die Suche die Sprache der Oberflaeche nicht kennt — und nicht kennen soll. */
function suchtextBauen(teile: readonly string[], straenge: readonly ProjektArt[], team: ProjektTeam | null, bueros: readonly string[]): string {
  return [
    ...teile,
    ...straenge.map((a) => projektArtName(a, "de")),
    ...straenge.map((a) => projektArtName(a, "en")),
    team ? projektTeamName(team, "de") : "",
    team ? projektTeamName(team, "en") : "",
    ...bueros,
  ]
    .filter((t) => t.length > 0)
    .join(" ")
    .toLowerCase();
}

/** Bueroliste, federfuehrendes zuerst — so steht es auch auf den Blaettern. */
function bueroReihenfolge(bueros: readonly { buero_id: string; federfuehrend: boolean }[]): string[] {
  return [...bueros].sort((a, b) => Number(b.federfuehrend) - Number(a.federfuehrend)).map((b) => b.buero_id);
}

export function stiftAusProjekt(
  p: ProjektKurz,
  farben: ReadonlyMap<string, string>,
  namen: Suchnamen = LEERE_NAMEN,
): Stift {
  const naechste = p.naechste_abgabe;
  const tage = naechste ? tageBis(naechste.faellig) : null;
  return {
    id: p.id,
    name: p.name,
    nummer: p.nummer,
    kuerzel: p.kuerzel,
    titel: projektTitel(p),
    praefix: projektPraefix(p),
    ort: p.ort,
    straenge: p.straenge,
    team: p.team,
    fortschritt: p.konzept_fortschritt,
    punkt: p.punkt,
    phaseId: p.haupt_phase_id,
    phasenIds: p.phasen,
    farbe: projektFarbe(p.bueros, farben),
    bueroIds: bueroReihenfolge(p.bueros),
    fuehrend: fuehrendesBuero(p.bueros),
    mitglieder: p.mitglieder.map((m) => m.user_id),
    bald: tage !== null && tage <= 7,
    tage,
    abgabeTitel: naechste ? naechste.titel : null,
    suchtext: suchtextBauen(
      [p.name, p.kuerzel, p.nummer, p.ort, naechste?.titel ?? "", namen.phasen.get(p.haupt_phase_id) ?? ""],
      p.straenge,
      p.team,
      p.bueros.map((b) => namen.bueros.get(b.buero_id) ?? ""),
    ),
    titelbild: p.titelbild_url,
    oeffentlich: p.oeffentlich,
    freigabe: p.freigabe,
    flaecheM2: p.flaeche_m2,
  };
}

export function stiftAusOeffentlich(
  p: OeffentlichProjekt,
  farben: ReadonlyMap<string, string>,
  namen: Suchnamen = LEERE_NAMEN,
): Stift {
  return {
    id: p.id,
    name: p.name,
    nummer: "",
    kuerzel: "",
    titel: p.name,
    praefix: "",
    ort: p.ort,
    straenge: p.straenge,
    team: p.team,
    fortschritt: null,
    punkt: p.punkt,
    phaseId: p.haupt_phase_id,
    // Nach aussen nur die Startphase — die parallelen Phasen sind Hausinterna.
    phasenIds: [p.haupt_phase_id],
    // Auch im Schaufenster die Buerofarbe (SPEC §12.3): `OeffentlichBuero`
    // traegt `farbe`, und die Karte draussen soll nicht anders aussehen als
    // die drinnen.
    farbe: projektFarbe(p.bueros, farben),
    bueroIds: bueroReihenfolge(p.bueros),
    fuehrend: fuehrendesBuero(p.bueros),
    mitglieder: [],
    bald: false,
    tage: null,
    abgabeTitel: null,
    suchtext: suchtextBauen(
      [p.name, p.ort, namen.phasen.get(p.haupt_phase_id) ?? ""],
      p.straenge,
      p.team,
      p.bueros.map((b) => namen.bueros.get(b.buero_id) ?? ""),
    ),
    titelbild: p.titelbild_url,
    oeffentlich: true,
    // `OeffentlichProjekt` traegt keine Freigabe (§36.7): das Schaufenster
    // liefert ohnehin nur Freigegebenes (`haus_sql`), und ein Feld, das dort
    // immer denselben Wert haette, verriete nur, dass es andere gibt.
    freigabe: "freigegeben",
    // `OeffentlichProjekt` traegt keine Flaeche; sie steht erst im Detail
    // (`/oeffentlich/projekte/{id}`), das die Schaufensterkarte selbst holt.
    flaecheM2: null,
  };
}

export function ortsmarken(
  bueros: readonly (Buero | OeffentlichBuero)[],
  personen: readonly Person[],
  stifte: readonly Stift[],
): Ortsmarke[] {
  return bueros.map((b) => ({
    id: b.id,
    name: b.name,
    farbe: b.farbe,
    punkt: b.punkt,
    // Zweitbueros zaehlen mit (SPEC §14.2): die Zahl an der Ortsmarke
    // beantwortet „wie viele gehoeren dazu" und nicht „wie viele Avatare
    // liegen hier".
    kopfzahl: personen.filter((p) => gehoertZuBuero(p, b.id)).length,
    projektZahl: stifte.filter((s) => s.bueroIds.includes(b.id)).length,
  }));
}

/** Alles, was die Kartenflaeche aus einem der beiden Staende herausliest. */
export interface Kartenstand {
  stifte: Stift[];
  marken: Ortsmarke[];
  personen: Person[];
  phasen: Phase[];
  abgaben: Abgabe[];
}

export const LEERER_STAND: Kartenstand = { stifte: [], marken: [], personen: [], phasen: [], abgaben: [] };

export function kartenstand(stand: Stand): Kartenstand {
  const farben = bueroFarbtafel(stand.bueros);
  const namen = suchnamen(stand.phasen, stand.bueros);
  const stifte = stand.projekte.map((p) => stiftAusProjekt(p, farben, namen));
  return {
    stifte,
    marken: ortsmarken(stand.bueros, stand.personen, stifte),
    personen: stand.personen,
    phasen: stand.phasen,
    abgaben: stand.abgaben,
  };
}

export function kartenstandOeffentlich(stand: OeffentlichStand): Kartenstand {
  const farben = bueroFarbtafel(stand.bueros);
  const namen = suchnamen(stand.phasen, stand.bueros);
  const stifte = stand.projekte.map((p) => stiftAusOeffentlich(p, farben, namen));
  return {
    stifte,
    marken: ortsmarken(stand.bueros, [], stifte),
    personen: [],
    phasen: stand.phasen,
    abgaben: [],
  };
}

/* ================================================================ Filter ==
   Die FORM steht in `store/ui.ts` (F1, `Filter` und `LEERER_FILTER`); hier
   steht nur, was sie auf der Karte BEDEUTET. Eine leere Liste heisst „alle"
   und nicht „keins" — sonst waere der erste Anblick der Karte eine leere
   Karte, und niemand faende den Weg zurueck. */

/** Trifft das Suchwort eine Person — Name, Alias, Titel oder eine ihrer Faehigkeiten (Steckbrief)? */
export function personTrifft(p: Person, wort: string): boolean {
  const w = wort.trim().toLowerCase();
  if (!w) return true;
  // Der ALIAS trifft mit (006): im Haus wird ein Teil der Belegschaft nur beim
  // Spitznamen gerufen, und wer „Fietje" sucht, soll nicht wissen muessen, dass
  // die Person im Verzeichnis „Friedrich Jakobsen" heisst.
  if (p.anzeigename.toLowerCase().includes(w) || p.alias.toLowerCase().includes(w) || p.titel.toLowerCase().includes(w)) return true;
  // Steckbrief v2 (SPEC §10.1): Werkzeug und Sprache treffen genauso wie das
  // Können, und „Frag mich zu …" ist die Zeile, auf die die Suche eigentlich
  // hinauswill — „wen kann ich zu Entwässerung fragen?".
  // `gerade` bleibt bewusst draussen: der Satz veraltet, und ein Treffer
  // darin liesse eine Person je nach Tagesform erscheinen und verschwinden.
  return (
    p.faehigkeiten.some((f) => f.toLowerCase().includes(w)) ||
    p.software.some((f) => f.toLowerCase().includes(w)) ||
    p.sprachen.some((f) => f.toLowerCase().includes(w)) ||
    p.frag_mich.some((f) => f.toLowerCase().includes(w))
  );
}

/**
 * Traegt die Person genau dieses Steckbriefwort? (SPEC §10.2)
 *
 * Genau, nicht enthalten: der Chip kommt aus einer Liste, also wird auch
 * gegen die Liste verglichen — „GIS" soll nicht „QGIS" mitleuchten lassen.
 * Ohne Ansehen der Schreibung, weil die Schreibung im Haus auseinandergeht.
 */
export function steckbriefTraegt(p: Person, art: SteckbriefArt, wort: string): boolean {
  const w = wort.toLocaleLowerCase();
  const liste = art === "faehigkeit" ? p.faehigkeiten : art === "software" ? p.software : p.sprachen;
  return liste.some((x) => x.toLocaleLowerCase() === w);
}

/** Die Kennungen aller Personen, die das Suchwort trifft — `null`, wenn nicht gesucht wird. */
export function personenTreffer(personen: readonly Person[], suche: string): ReadonlySet<string> | null {
  const w = suche.trim();
  if (!w) return null;
  return new Set(personen.filter((p) => personTrifft(p, w)).map((p) => p.id));
}

/**
 * `treffer`: Personen, die das Suchwort trifft (Name, Titel, Faehigkeit). Ein
 * Projekt bleibt dann auch stehen, wenn eine solche Person im Team ist —
 * „QGIS" zeigt die Projekte derer, die QGIS koennen.
 */
export function passtZuFilter(s: Stift, f: Filter, ichId: string | null, treffer: ReadonlySet<string> | null = null): boolean {
  if (f.bueros.length > 0 && !s.bueroIds.some((b) => f.bueros.includes(b))) return false;
  if (f.phasen.length > 0 && !f.phasen.includes(s.phaseId)) return false;
  if (f.personen.length > 0 && !s.mitglieder.some((u) => f.personen.includes(u))) return false;
  if (f.arten.length > 0 && !s.straenge.some((a) => f.arten.includes(a))) return false;
  // Ohne Team faellt ein Projekt heraus, sobald ein Team gewaehlt ist — `null`
  // ist kein fuenftes Team, sondern die Abwesenheit einer Zuordnung.
  if (f.teams.length > 0 && (s.team === null || !f.teams.includes(s.team))) return false;
  if (f.baldFaellig && !s.bald) return false;
  if (f.nurOeffentlich && !s.oeffentlich) return false;
  if (f.meine) {
    if (!ichId) return false;
    if (!s.mitglieder.includes(ichId)) return false;
  }
  if (f.suche.trim()) {
    // Das Suchfeld der Kopfleiste schreibt hierher (F1). Auf der Karte
    // trifft es Name, Kuerzel, Nummer und Ort — Personen haben ihren eigenen
    // Filter. Das Kuerzel steht dabei (SPEC §11.1): im Haus heisst ein Projekt
    // im Gespraech nach dem Kuerzel und nicht nach seinem vollen Namen.
    // Seit dem 18.09. trifft es ausserdem Phase, Projektart, Team, Bueros und
    // die naechste Abgabe — alles steht vorgebaut in `suchtext`.
    const wort = f.suche.trim().toLowerCase();
    const imTeam = treffer !== null && s.mitglieder.some((id) => treffer.has(id));
    if (!s.suchtext.includes(wort) && !imTeam) return false;
  }
  return true;
}

export function istFilterLeer(f: Filter): boolean {
  return (
    f.bueros.length === 0 &&
    f.phasen.length === 0 &&
    f.personen.length === 0 &&
    f.arten.length === 0 &&
    f.teams.length === 0 &&
    !f.meine &&
    !f.baldFaellig &&
    !f.nurOeffentlich &&
    f.suche.trim() === ""
  );
}

/**
 * Der Filter, der im SCHAUFENSTER wirkt: nur, was man dort auch bedienen kann —
 * Art und Team (Kopf am Rechner, Chips am Telefon) und am Telefon die Bueros
 * (`MobilChips`). Alles andere gilt dort nicht.
 *
 * Fehlerrunde 01.10.2026, #22: der Filter ist EIN Speicher fuer alle Bildschirme
 * und ueberlebt das Abmelden. Wer angemeldet „Meine" oder „Abgabe ≤ 7 T"
 * einschaltete oder in der Kopfleiste suchte und sich dann abmeldete, sah auf
 * „/" eine leere Karte („0 Projekte"): `meine` trifft ohne `ichId` nie, `bald`
 * ist draussen immer falsch — und das Schaufenster hat fuer keinen dieser
 * Filter einen Schalter, also auch kein Zuruecksetzen. Erst Neuladen half.
 *
 * Ein Literal statt `{ ...f, … }`: kommt ein Feld zum Filter dazu, meldet der
 * Typpruefer sich HIER, und jemand muss entscheiden, ob es draussen gilt.
 */
export function schaufensterFilter(f: Filter, schmal: boolean): Filter {
  return {
    bueros: schmal ? f.bueros : [],
    phasen: [],
    personen: [],
    arten: f.arten,
    teams: f.teams,
    meine: false,
    baldFaellig: false,
    nurOeffentlich: false,
    suche: "",
  };
}

/* ================================================================= Boegen ==
   Eine quadratische Bezierkurve in Laenge/Breite (E7/T7). Der Stuetzpunkt
   liegt auf der Mitte der Sehne, seitlich versetzt — dadurch bekommt die
   Linie ihren Bogen.

   Der Versatz wird in einem laengenkorrigierten Raum gerechnet: ein Grad
   Laenge ist auf 53° Breite nur noch 0,6 Grad Breite breit, und ohne die
   Korrektur waere der „senkrechte" Versatz schief und der Bogen je nach
   Himmelsrichtung verschieden stark.

   EHRLICHE GRENZE: eine `line`-Ebene liegt auf der Bodenflaeche. In der
   gekippten Ansicht liest sich die Kurve als eine auf die Karte GEZEICHNETE
   Kurve, nicht als ein Kabel, das durch die Luft spannt — MapLibre 6 kennt
   keinen Hoehenversatz fuer Linien (RESEARCH §4.1, nur `symbol-height-offset`
   fuer Zeichen). Der Entwurf zeigt den Luftbogen; das ist der eine Punkt, an
   dem die Umsetzung hinter der Entwurfsmappe zurueckbleibt. */

export const BOGEN_PUNKTE = 48;

/**
 * Die Punkte eines Bogens von `von` nach `nach`.
 * `hub` ist die Auslenkung als Anteil der Sehnenlaenge.
 */
export function bogenPunkte(von: LonLat, nach: LonLat, anzahl = BOGEN_PUNKTE, hub = 0.18): LonLat[] {
  const [lon0, lat0] = von;
  const [lon1, lat1] = nach;
  // Laengenkorrektur auf der mittleren Breite der Strecke.
  const k = Math.cos(((lat0 + lat1) / 2) * (Math.PI / 180)) || 1;
  const dx = (lon1 - lon0) * k;
  const dy = lat1 - lat0;
  const laenge = Math.hypot(dx, dy);
  // Zwei Punkte am selben Ort haetten keine Richtung — dann eine gerade
  // Strecke statt einer Division durch null.
  if (laenge < 1e-9) return [von, nach];
  // Senkrechte zur Sehne, immer auf dieselbe Seite: so faechern die Boegen
  // eines Bueros auf, statt sich zu ueberlagern.
  const nx = -dy / laenge;
  const ny = dx / laenge;
  const h = laenge * hub;
  const cx = (lon0 + lon1) / 2 + ((nx * h) / k);
  const cy = (lat0 + lat1) / 2 + ny * h;

  const n = Math.max(2, Math.floor(anzahl));
  const punkte: LonLat[] = [];
  for (let i = 0; i < n; i += 1) {
    const t = i / (n - 1);
    const u = 1 - t;
    punkte.push([
      u * u * lon0 + 2 * u * t * cx + t * t * lon1,
      u * u * lat0 + 2 * u * t * cy + t * t * lat1,
    ]);
  }
  return punkte;
}

/**
 * Der Schluessel eines Farbpaars. `line-gradient` ist NICHT datengesteuert —
 * ein Verlauf gilt fuer die ganze Ebene. Deshalb bekommt jedes vorkommende
 * Paar (Heimatbuero → Projektfarbe, seit 007 ebenfalls eine Buerofarbe) eine
 * eigene Quelle und eine eigene Ebene, und dieser Schluessel benennt sie. Kleingeschrieben, damit "#AABBCC" und
 * "#aabbcc" nicht zwei Ebenen ergeben.
 */
export function farbPaar(von: string, nach: string): string {
  return `${von}_${nach}`.toLowerCase().replace(/[^a-z0-9_]/g, "");
}

/* ================================================================= Kamera ==  */

export const EUROPA_MITTE: LonLat = [11.4, 49.6];
export const EUROPA_ZOOM = 3.6;

export interface Kasten {
  west: number;
  sued: number;
  ost: number;
  nord: number;
}

/** Umschliessender Kasten aller Punkte, oder `null` bei keinem Punkt. */
export function kastenUm(punkte: readonly LonLat[]): Kasten | null {
  let west = Infinity;
  let sued = Infinity;
  let ost = -Infinity;
  let nord = -Infinity;
  for (const p of punkte) {
    const lon = p[0];
    const lat = p[1];
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
    if (lon < west) west = lon;
    if (lon > ost) ost = lon;
    if (lat < sued) sued = lat;
    if (lat > nord) nord = lat;
  }
  if (!Number.isFinite(west) || !Number.isFinite(sued)) return null;
  // Ein einzelner Punkt ergaebe einen Kasten ohne Flaeche; `fitBounds`
  // zoomte darauf bis zum Anschlag. Ein kleiner Saum haelt das auf.
  if (ost - west < 0.02) {
    west -= 0.01;
    ost += 0.01;
  }
  if (nord - sued < 0.02) {
    sued -= 0.01;
    nord += 0.01;
  }
  return { west, sued, ost, nord };
}

/* =============================================== Plaetze der Avatare (N1) ==
   Alles in BILDPUNKTEN auf dem Schirm, relativ zu einem Bezugspunkt. Die
   Zahlen stammen aus `.design/karte.mjs`; dort steht dieselbe Rechnung fuer
   das gezeichnete Bild.                                                     */

/** Radius eines Avatars: 16 px am Rechner, 11 px am Telefon (Entwurfsmappe). */
export const AVATAR_R = 16;
export const AVATAR_R_SCHMAL = 11;

/**
 * Die Bueromarke in Bildpunkten, wie karte.css sie zeichnet (`.k-buero`):
 * das Haus (26 px breit) steht UEBER dem Namensschild (22 px hoch, 5 px
 * Luecke), und die Marke haengt mit ihrer Unterkante am Buero-Punkt
 * (marken.ts, `anchor: "bottom"`). Wer dort Groessen aendert, aendert sie hier.
 */
const HAUS_HALB = 13;
const SCHILD_FREI = 22 + 5;
/** Luft zwischen Hausrand und erster Scheibe — im Entwurf dieselben 4 px. */
const HAUS_LUFT = 4;

/**
 * Wabenstapel neben dem Heimatbuero: zwei Reihen, die obere um eine halbe
 * Spalte versetzt — das ist die Wabe aus dem Entwurf. `i` ist der Platz in
 * der Liste der Personen dieses Bueros.
 *
 * Der Stapel steht NEBEN DEM HAUS, nicht auf dem Namensschild (28.09.2026,
 * Rauchtest: die Scheiben deckten „Hamburg" bis auf „Hambu"). Der Entwurf
 * (`.design/karte.mjs`) rechnet seine Hoehen vom Fuss des Hauses aus, weil
 * dort das Haus AUF dem Punkt steht und das Schild darunter haengt; hier
 * steht der Hausfuss `SCHILD_FREI` hoeher. Genau darum wird der Stapel
 * gehoben — ein fester Versatz des Ankers, an der Physik aendert das nichts
 * (Wabenabstand und Kollisionsradius bleiben, siehe avatare.ts). Seitlich
 * beginnt er am Hausrand statt bei `1,75 r`: das Haus ist mit 26 px breiter
 * als im Entwurf (16 px), und am Telefon (r = 11) laege die erste Scheibe
 * sonst 5 px auf dem Haus.
 */
export function stapelPlatz(i: number, r: number): [number, number] {
  const reihe = i % 2;
  const spalte = Math.floor(i / 2);
  const g = 2.12 * r;
  const links = HAUS_HALB + HAUS_LUFT + r;
  return [links + (spalte + reihe * 0.5) * g, -(0.75 + reihe * 0.9) * g * 0.95 + 4 - SCHILD_FREI];
}

/**
 * Umlaufbahn um einen Projektstift (E6). Der Faecher oeffnet nach oben; bei
 * grossen Teams wird der Schritt kleiner und der Radius groesser, statt dass
 * sich die Scheiben ueberrunden.
 */
export function bahnPlatz(i: number, anzahl: number, r: number): [number, number] {
  const n = Math.max(1, anzahl);
  const schritt = n <= 8 ? 40 : Math.max(18, 320 / n);
  const radius = Math.max(2.75 * r, (n * schritt * r * 2.2) / 360);
  const grad = -90 + (i - (n - 1) / 2) * schritt;
  const bogen = grad * (Math.PI / 180);
  return [Math.cos(bogen) * radius, Math.sin(bogen) * radius];
}

/* ===================================================== Namensplakette ==
   Wohin die Plakette am Stift faellt (`NamensMarke` in marken.ts). Rechnung
   hier, Wirkung dort — so laesst sie sich ohne Karte pruefen.

   Fehlerrunde 01.10.2026 (T1/T2): die Plakette hing fest UNTER dem Stift
   (`anchor: "top"`), ohne jede Ruecksicht auf den Rand. Ein Stift weit unten
   schob sie unter die Reiterleiste oder aus dem Bild, einer am Rand halb
   hinaus. Jetzt gilt eine einfache Regel (Lead-Entscheidung nach der
   Recherche): unter dem Stift, solange sie dort Platz hat — sonst darueber;
   seitlich so weit eingerueckt, dass sie im Bild bleibt, aber nie so weit,
   dass sie sich vom Stift loest. Keine Kamerafahrt, kein Skalieren mit dem
   Zoom: die Groesse steht in Bildschirmpixeln. */

/** Abstand der Plakette vom Stiftpunkt in px, unter wie ueber dem Stift.
 *  Muss zu `.k-name-karte` (`top` bzw. `bottom`) in karte.css passen. */
export const PLAKETTE_ABSTAND = 14;

/** So weit ragt die Plakette mindestens ueber den Stift hinaus, wenn sie zur
 *  Seite ausweicht — sonst stuende sie neben ihm statt an ihm. */
const PLAKETTE_HALT = 16;

/** Der freie Teil der Karte, in Bildpunkten des Kartenbehaelters (0|0 links oben). */
export interface PlakettenRaum {
  oben: number;
  unten: number;
  links: number;
  rechts: number;
}

export interface PlakettenLage {
  /** Ueber dem Stift statt darunter. */
  oben: boolean;
  /** Seitlicher Versatz in px, negativ nach links. */
  schub: number;
}

export function plakettenLage(
  stift: { x: number; y: number },
  groesse: { breite: number; hoehe: number },
  raum: PlakettenRaum,
  abstand = PLAKETTE_ABSTAND,
): PlakettenLage {
  const platzUnten = raum.unten - (stift.y + abstand);
  const platzOben = stift.y - abstand - raum.oben;
  // Unten ist der Normalfall. Nach oben nur, wenn sie unten NICHT passt —
  // und oben passt oder dort wenigstens mehr Platz ist als unten.
  const oben = platzUnten < groesse.hoehe && (platzOben >= groesse.hoehe || platzOben > platzUnten);
  const halb = groesse.breite / 2;
  let schub = 0;
  if (stift.x - halb < raum.links) schub = raum.links - (stift.x - halb);
  else if (stift.x + halb > raum.rechts) schub = raum.rechts - (stift.x + halb);
  const grenze = Math.max(0, halb - PLAKETTE_HALT);
  schub = Math.max(-grenze, Math.min(grenze, schub));
  // `|| 0`: aus -0,4 wird sonst -0, und das ist fuer `Object.is` keine 0.
  return { oben, schub: Math.round(schub) || 0 };
}

/** Luft zwischen Plakette und Oberkante des Auswahlblatts, in px. */
export const BLATT_LUFT = 8;

/**
 * Der freie Raum, wenn am Telefon das Auswahlblatt (`.k-sheet`) offen steht:
 * unten endet er dann an dessen Oberkante (abzueglich `BLATT_LUFT`), nicht an
 * der Reiterleiste. `blattOben` in Bildpunkten des Kartenbehaelters, `null`
 * ohne Blatt.
 *
 * Fehlerrunde 01.10.2026, Nacharbeit zu T2: am Telefon steht die Plakette nur
 * bei einer Auswahl — und genau dann ist auch das Blatt offen, im Nachlauf
 * nach dem Schliessen des Projektblatts ebenso. Der Messkasten `.k-name-raum`
 * kennt nur die Reiterleiste; das Blatt (z-index 5) lag darueber. Die Regel
 * hielt den Platz unter dem Stift darum fuer frei, klappte nicht, und die
 * Bildkarte verschwand ganz oder halb unter dem Blatt.
 */
export function raumUeberBlatt(raum: PlakettenRaum, blattOben: number | null): PlakettenRaum {
  if (blattOben === null) return raum;
  return { ...raum, unten: Math.min(raum.unten, blattOben - BLATT_LUFT) };
}

/* =============================================================== Sonstiges == */

export function nachId<T extends { id: string }>(xs: readonly T[]): Map<string, T> {
  const m = new Map<string, T>();
  for (const x of xs) m.set(x.id, x);
  return m;
}

/**
 * Die sechs naechsten offenen Abgaben, nach Faelligkeit. Die Entwurfsmappe
 * zeigt genau sechs Zeilen im rechten Blatt.
 */
export function naechsteAbgaben(abgaben: readonly Abgabe[], stifte: readonly Stift[], anzahl = 6): Abgabe[] {
  const bekannt = new Set(stifte.map((s) => s.id));
  return abgaben
    .filter((a) => a.status !== "abgegeben" && bekannt.has(a.projekt_id))
    .sort((a, b) => (a.faellig < b.faellig ? -1 : a.faellig > b.faellig ? 1 : 0))
    .slice(0, anzahl);
}

/** Eine CSS-Variable als Farbwert — MapLibre kennt `var(--…)` nicht. */
export function cssWert(name: string, ersatz: string): string {
  if (typeof window === "undefined") return ersatz;
  try {
    const wert = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return wert || ersatz;
  } catch {
    return ersatz;
  }
}

/* ======================================== Setzmodus und Nähe (§37.9) ==
   Das Plus der Karte setzt seit §37 zwei Dinge per Tipp: ein neues Projekt
   (wie bisher) und einen Beitrag mit Ort. Der Modus war bis dahin ein
   boolesches `setztPunkt`; ein zweites Boolean daneben ließe den Zustand
   „beides zugleich" zu, den es nicht geben darf. */

/** Was ein Tipp auf die Karte gerade setzt — `null`: nichts, die Karte ist normal bedienbar. */
export type SetzModus = null | "projekt" | "beitrag";

/** Mittlerer Erdradius (IUGG, R₁) in Metern — derselbe wie in Turf. */
const ERDRADIUS_M = 6_371_008.8;

/**
 * Großkreisabstand zweier Punkte in Metern (Haversine). Für „das nächste
 * Projekt in 1 km" genau genug: die Kugel weicht vom Ellipsoid um höchstens
 * ~0,5 % ab, und die Grenze ist eine Daumenregel, keine Vermessung.
 * Die asin-Form mit `min(1, …)`: bei Gegenpunkten liefe die Wurzel durch
 * Rundung knapp über 1, und `asin` gäbe `NaN`.
 */
export function abstandMeter(a: LonLat, b: LonLat): number {
  const rad = Math.PI / 180;
  const b1 = a[1] * rad;
  const b2 = b[1] * rad;
  const db = (b[1] - a[1]) * rad;
  const dl = (b[0] - a[0]) * rad;
  const h = Math.sin(db / 2) ** 2 + Math.cos(b1) * Math.cos(b2) * Math.sin(dl / 2) ** 2;
  return 2 * ERDRADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Das nächste FREIGEGEBENE Projekt innerhalb von `grenzeMeter` — oder `null`.
 *
 * Zwei Aufrufer: „Beitrag mit Ort" wählt damit das Projekt vor (1 km, §37.9),
 * der Dialog „Ansicht speichern" fällt darauf zurück, wenn es kein zuletzt
 * aktives Projekt gibt (`Infinity`, §37.7).
 *
 * Nur freigegebene, weil `/stand` der anlegenden Person auch ihre offenen
 * Einreichungen zeigt (§36) — der Server nimmt einen Beitrag aber nur an
 * einem freigegebenen Projekt an (422 `PROJEKT_WEG`), und eine Vorwahl, die
 * beim Absenden scheitert, ist schlechter als keine. Bei Gleichstand gewinnt
 * das erste in der Liste (strenges `<`), damit die Wahl nicht zwischen zwei
 * gleich nahen Projekten hin und her springt.
 */
export function naechstesProjekt(
  punkt: LonLat,
  projekte: readonly ProjektKurz[],
  grenzeMeter: number,
): ProjektKurz | null {
  let bestes: ProjektKurz | null = null;
  let bester = Infinity;
  for (const p of projekte) {
    if (p.freigabe !== "freigegeben") continue;
    const d = abstandMeter(punkt, p.punkt);
    // `!(d <= …)` statt `d > …`: ein `NaN` (kaputte Koordinate) fällt so heraus.
    if (!(d <= grenzeMeter)) continue;
    if (d < bester) {
      bestes = p;
      bester = d;
    }
  }
  return bestes;
}
